import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AppIcon from './AppIcon';
import OrnamentalDivider from './OrnamentalDivider';
import { getWedding, updateWedding } from '../../services/wedding';
import { useCurrency, CurrencyCode, CURRENCY_OPTIONS } from '../../context/CurrencyContext';
import { Planner, getPlanners, createPlanner, updatePlanner, deletePlanner } from '../../services/planners';

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const COLOR_OPTIONS = [
  '#B07A8F', '#C9A96E', '#6B9E78', '#6A8EB0', '#D4A84E',
  '#E57373', '#9575CD', '#4FC3F7', '#81C784', '#FF8A65',
  '#A1887F', '#90A4AE',
];

const SettingsDialog: React.FC<SettingsDialogProps> = ({ open, onClose, onSaved }) => {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [venue, setVenue] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [error, setError] = useState('');

  // Currency
  const { currency, setCurrency } = useCurrency();
  const [localCurrency, setLocalCurrency] = useState<CurrencyCode>(currency);

  // Planners
  const [planners, setPlanners] = useState<Planner[]>([]);
  const [editingPlanner, setEditingPlanner] = useState<Planner | null>(null);
  const [plannerName, setPlannerName] = useState('');
  const [plannerEmail, setPlannerEmail] = useState<string>('');
  const [plannerColor, setPlannerColor] = useState<string | null>(null);
  const [addingPlanner, setAddingPlanner] = useState(false);

  const loadPlanners = async () => {
    try {
      const res = await getPlanners();
      setPlanners(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (open) {
      getWedding().then((res) => {
        setTitle(res.data.title);
        setDate(res.data.date || '');
        setVenue(res.data.venue || '');
        if (res.data.currency) setLocalCurrency(res.data.currency as CurrencyCode);
      });
      loadPlanners();
      setEditingPlanner(null);
      setAddingPlanner(false);
    }
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await updateWedding({
        title,
        date: date || null,
        venue: venue || null,
        currency: localCurrency,
      } as any);
      setCurrency(localCurrency);
      setSnackOpen(true);
      onClose();
      onSaved?.();
    } catch (err: any) {
      console.error('Settings save error:', err?.response?.data || err);
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map((d: any) => d.msg || String(d)).join('; '));
      } else {
        setError('Failed to save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const startAddPlanner = () => {
    setAddingPlanner(true);
    setEditingPlanner(null);
    setPlannerName('');
    setPlannerEmail('');
    setPlannerColor(null);
  };

  const startEditPlanner = (p: Planner) => {
    setEditingPlanner(p);
    setAddingPlanner(false);
    setPlannerName(p.name);
    setPlannerEmail(p.email || '');
    setPlannerColor(p.color);
  };

  const cancelPlannerEdit = () => {
    setEditingPlanner(null);
    setAddingPlanner(false);
    setPlannerName('');
    setPlannerEmail('');
    setPlannerColor(null);
  };

  const savePlanner = async () => {
    if (!plannerName.trim()) return;
    const emailValue = plannerEmail.trim() || null;
    try {
      if (editingPlanner) {
        await updatePlanner(editingPlanner.id, { name: plannerName.trim(), email: emailValue, color: plannerColor });
      } else {
        await createPlanner({ name: plannerName.trim(), email: emailValue, color: plannerColor });
      }
      cancelPlannerEdit();
      loadPlanners();
      onSaved?.();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeletePlanner = async (p: Planner) => {
    try {
      await deletePlanner(p.id);
      if (editingPlanner?.id === p.id) cancelPlannerEdit();
      loadPlanners();
      onSaved?.();
    } catch (err) {
      console.error(err);
    }
  };

  const isEditingOrAdding = addingPlanner || !!editingPlanner;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
            )}

            {/* Wedding details */}
            <Typography variant="subtitle2" color="text.secondary">Wedding Details</Typography>
            <TextField
              label="Wedding Title"
              fullWidth
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              label="Wedding Date"
              type="date"
              fullWidth
              value={date}
              onChange={(e) => setDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              label="Venue"
              fullWidth
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
            />

            <OrnamentalDivider />

            {/* Currency */}
            <Typography variant="subtitle2" color="text.secondary">Currency</Typography>
            <ToggleButtonGroup
              value={localCurrency}
              exclusive
              onChange={(_, val) => { if (val) setLocalCurrency(val as CurrencyCode); }}
              size="small"
            >
              {CURRENCY_OPTIONS.map((opt) => (
                <ToggleButton key={opt.code} value={opt.code} sx={{ px: 2, textTransform: 'none' }}>
                  {opt.symbol} {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <OrnamentalDivider />

            {/* Planners */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">Wedding Planners</Typography>
              {!isEditingOrAdding && (
                <Button size="small" startIcon={<AppIcon name="add" />} onClick={startAddPlanner}>
                  Add
                </Button>
              )}
            </Box>

            {planners.length === 0 && !isEditingOrAdding && (
              <Typography variant="body2" color="text.secondary">No planners yet.</Typography>
            )}

            <List disablePadding>
              {planners.map((p) => (
                editingPlanner?.id === p.id ? (
                  <PlannerEditRow
                    key={p.id}
                    name={plannerName}
                    email={plannerEmail}
                    color={plannerColor}
                    onNameChange={setPlannerName}
                    onEmailChange={setPlannerEmail}
                    onColorChange={setPlannerColor}
                    onSave={savePlanner}
                    onCancel={cancelPlannerEdit}
                  />
                ) : (
                  <ListItem
                    key={p.id}
                    sx={{
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      mb: 1,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      py: 1.5,
                    }}
                    secondaryAction={
                      !isEditingOrAdding && (
                        <Box>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => startEditPlanner(p)}>
                              <AppIcon name="edit" fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => handleDeletePlanner(p)} sx={{ color: 'error.main' }}>
                              <AppIcon name="delete-outline" fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )
                    }
                  >
                    <Chip
                      label={p.name}
                      size="small"
                      sx={{
                        bgcolor: p.color ? `${p.color}25` : 'grey.100',
                        color: p.color || 'grey.800',
                        fontWeight: 500,
                        border: `1px solid ${p.color || '#E4DED8'}`,
                      }}
                    />
                  </ListItem>
                )
              ))}
              {addingPlanner && (
                <PlannerEditRow
                  name={plannerName}
                  email={plannerEmail}
                  color={plannerColor}
                  onNameChange={setPlannerName}
                  onEmailChange={setPlannerEmail}
                  onColorChange={setPlannerColor}
                  onSave={savePlanner}
                  onCancel={cancelPlannerEdit}
                />
              )}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ color: 'grey.600' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !title.trim()}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackOpen}
        autoHideDuration={2000}
        onClose={(_, reason) => { if (reason !== 'clickaway') setSnackOpen(false); }}
      >
        <Alert severity="success" onClose={() => setSnackOpen(false)} variant="filled">
          Settings saved!
        </Alert>
      </Snackbar>
    </>
  );
};

// Inline edit/add row for a planner
const PlannerEditRow: React.FC<{
  name: string;
  email: string;
  color: string | null;
  onNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  onColorChange: (v: string | null) => void;
  onSave: () => void;
  onCancel: () => void;
}> = ({ name, email, color, onNameChange, onEmailChange, onColorChange, onSave, onCancel }) => (
  <Box sx={{
    bgcolor: 'grey.50',
    borderRadius: 2,
    mb: 1,
    border: '1px solid',
    borderColor: 'primary.main',
    p: 2,
  }}>
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
      <TextField
        size="small"
        placeholder="Planner name"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && name.trim() && onSave()}
        autoFocus
        sx={{ flex: 1 }}
      />
      <TextField
        size="small"
        placeholder="Email (Google account)"
        type="email"
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        sx={{ flex: 1 }}
      />
      <Tooltip title="Save">
        <IconButton size="small" onClick={onSave} disabled={!name.trim()} color="primary">
          <AppIcon name="check" fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Cancel">
        <IconButton size="small" onClick={onCancel}>
          <AppIcon name="close" fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
      {COLOR_OPTIONS.map((c) => (
        <Box
          key={c}
          onClick={() => onColorChange(color === c ? null : c)}
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            bgcolor: c,
            cursor: 'pointer',
            border: color === c ? '3px solid' : '2px solid transparent',
            borderColor: color === c ? 'grey.800' : 'transparent',
            outline: color === c ? '2px solid white' : 'none',
            outlineOffset: -3,
            transition: 'transform 0.15s ease',
            '&:hover': { opacity: 0.8, transform: 'scale(1.15)' },
          }}
        />
      ))}
    </Box>
  </Box>
);

export default SettingsDialog;
